import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'
import { differenceInDays, format, subDays } from 'date-fns'

import { leadInitialized } from 'common/actions'
import { CommonState } from 'embed/reducers'
import { DEPOSITS_PRODUCT_TYPES_LIST, PublicClassNames } from 'embed/constants'
import { isDev } from 'embed/constants/config'
import { offerStyleIsCompactSelector } from 'embed/selectors'
import { formatCurrency, formatImageUrl, getContrastingColor, getOfferNumericFieldValue, padRate } from 'embed/utils'

import Modal from 'embed/components/Modal'
import TrackingPixel from 'embed/components/TrackingPixel'
import Session from 'common/services/Session'
import { DepositsOffer } from 'embed/constants/models'
import { Product } from 'even-ts-static'
import { useTheme } from 'embed/hooks'

const compactStyles = require('embed/styles/resultscompact')
const styles = require('embed/styles/results')

const APY_POSTFIX = '%'

const MINIMUMS_PREFIX = '$'

const MARCUS_OFFER_UUID = '3e72c075-0dd7-45bd-9464-214256fd5909'

export interface DispatchProps {
  dispatchLeadInitialized: (leadUuid: string) => void
}

export interface OwnProps {
  bestOfferId?: string
  dispatchOfferCta?: (loan: DepositsOffer) => void
  offer: DepositsOffer
  useCompactListItem: boolean
  viewportHeight?: number
  viewportWidth?: number
}

export interface StoreProps {
  leadUuid?: string
  rateTableUuid?: string
  offerStyleIsCompact: boolean
}

export type ListItemProps = OwnProps & DispatchProps & StoreProps

export interface ListItemState {
  disclaimerModalOffset: number
  disclaimerModalVisible: boolean
}

const PartnerLogo = ({ dispatchOfferCta, offer, offerStyleIsCompact }) => {
  const partnerLogoClassName = classnames(styles.partnerImage, { [styles.partnerImageSmall]: offerStyleIsCompact })
  const depositsOfferCta = (event: React.MouseEvent<Element>) => {
    // Prevent multiple windows from opening on offer click due to multiple click targets
    event.stopPropagation()
    dispatchOfferCta?.(offer)
  }
  const logoImage = (
    <img alt={offer?.details?.name} className={partnerLogoClassName} src={formatImageUrl(offer?.partner?.imageUrl)} />
  )
  if (dispatchOfferCta === undefined) {
    return logoImage
  } else {
    return (
      <a
        className={classnames(
          styles.partnerLogoLink,
          css`
            margin: auto;
          `,
        )}
        onClick={depositsOfferCta}
      >
        {logoImage}
      </a>
    )
  }
}

interface DisclaimerModalProps {
  offset: number
  toggleModal: (event: React.MouseEvent<Element>) => void
  disclaimerMoreInfo?: string
  disclaimerDetails?: string
}

const DisclaimerModal: React.FC<DisclaimerModalProps> = ({
  offset,
  toggleModal,
  disclaimerMoreInfo,
  disclaimerDetails,
}) => {
  const { fontFamilyPrimary } = useTheme()
  return (
    <Modal offset={offset} onDismiss={toggleModal}>
      {disclaimerDetails && (
        <div className={styles.modalDisclaimer}>
          <h4
            className={css`
              font-family: ${fontFamilyPrimary};
            `}
          >
            More Information
          </h4>
          <p dangerouslySetInnerHTML={{ __html: disclaimerDetails }} />
        </div>
      )}
      {disclaimerMoreInfo && (
        <div className={styles.modalDisclaimer}>
          <h4
            className={css`
              font-family: ${fontFamilyPrimary};
            `}
          >
            Disclaimer
          </h4>
          <p dangerouslySetInnerHTML={{ __html: disclaimerMoreInfo }} />
        </div>
      )}
    </Modal>
  )
}

const ListItem: React.FC<ListItemProps> = ({
  dispatchOfferCta,
  leadUuid,
  offer,
  offerStyleIsCompact,
  rateTableUuid,
}) => {
  const el: React.MutableRefObject<HTMLLIElement | null> = React.useRef(null)
  const [disclaimerModalOffset, setDisclaimerModalOffset] = React.useState(0)
  const [disclaimerModalVisible, setDisclaimerModalVisible] = React.useState(false)

  const depositsOfferCta = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      // Prevent multiple windows from opening on offer click due to multiple click targets
      event.stopPropagation()
      dispatchOfferCta?.(offer)
    },
    [dispatchOfferCta, offer],
  )

  const toggleDisclaimerModal = React.useCallback(
    (event: React.MouseEvent<Element>) => {
      event.stopPropagation()
      setDisclaimerModalOffset(el.current?.offsetTop ?? 0)
      setDisclaimerModalVisible(!disclaimerModalVisible)
    },
    [setDisclaimerModalOffset, setDisclaimerModalVisible, disclaimerModalVisible, el],
  )

  let customPixelSrc
  switch (offer.uuid) {
    case MARCUS_OFFER_UUID:
      customPixelSrc =
        `https://ad.doubleclick.net/ddm/trackimp/N450204.3500800EVENFINANCIAL/B22081611.241231345;` +
        `dc_trk_aid=438129142;dc_trk_cid=112862880;u=${Session.getLeadUuid()};ord=${Date.now()};` +
        `dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=?`
      break

    default:
  }
  const customPixel = customPixelSrc ? <img src={customPixelSrc} /> : null

  const disclaimerDetails = offer?.details?.description
  const disclaimerMoreInfo = offer?.partner?.disclaimer

  // APY Data
  const annualPercentYield = getOfferNumericFieldValue(offer.details.annualPercentYield, {
    postfix: APY_POSTFIX,
    transformValue: padRate,
  })

  // Minimums data
  const avoidFees = getOfferNumericFieldValue(offer.details.minimumDepositWithFees, {
    prefix: MINIMUMS_PREFIX,
    transformValue: formatCurrency,
  })
  const checkWriting = offer.details.checkWriting === true ? 'Yes' : 'No'

  const offerEffectiveAsDate = new Date(offer.details.effectiveAsOf)
  const effectiveAsOfDate =
    differenceInDays(new Date(), offerEffectiveAsDate) > 7
      ? format(subDays(new Date(), 2), 'MM/dd/yyyy')
      : format(offerEffectiveAsDate, 'MM/dd/yyyy')

  const minimumDeposit = getOfferNumericFieldValue(offer.details.minimumDeposit, {
    prefix: MINIMUMS_PREFIX,
    transformValue: formatCurrency,
  })
  const monthlyFees = getOfferNumericFieldValue(offer.details.monthlyFee, {
    prefix: MINIMUMS_PREFIX,
    transformValue: formatCurrency,
  })

  // Product SubType
  const productSubType = DEPOSITS_PRODUCT_TYPES_LIST.find((productType) => productType.value === offer.productSubType)
  const productSubTypeLabel = productSubType?.label

  const {
    borderRadiusLarge,
    borderRadiusSmall,
    colorBorder,
    ctaStyles,
    colorTextPrimary,
    colorTextSecondary,
    fontFamilyPrimary,
  } = useTheme()

  const compactListItem = (
    <li ref={el} data-test-val={isDev ? offer.uuid : ''}>
      <TrackingPixel leadUuid={leadUuid} rateTableUuid={rateTableUuid} offerUuid={offer.uuid} />
      <div
        className={classnames(
          compactStyles.offerWrapper,
          compactStyles.deposits,
          css`
            border-color: ${colorBorder};
          `,
        )}
      >
        <div className={styles.productDetails}>
          <div className={classnames(styles.label, styles.productSubtype, css(`color: ${colorTextSecondary}`))}>
            {productSubTypeLabel}
          </div>
          <div
            className={classnames(
              styles.label,
              styles.effectiveDate,
              compactStyles.effectiveDate,
              css(`color: ${colorTextSecondary}`),
            )}
          >
            As of {effectiveAsOfDate}
          </div>
        </div>
        <div className={classnames(compactStyles.column, compactStyles.partnerLogo)}>
          <PartnerLogo dispatchOfferCta={dispatchOfferCta} offer={offer} offerStyleIsCompact={offerStyleIsCompact} />
        </div>
        <div className={classnames(compactStyles.column, compactStyles.details)}>
          <div className={compactStyles.apy}>
            <div
              className={classnames(
                compactStyles.bigStat,
                css`
                  font-family: ${fontFamilyPrimary};
                `,
              )}
            >
              {annualPercentYield}
            </div>
            <div
              className={css`
                color: ${colorTextSecondary};
              `}
            >
              APY
            </div>
          </div>
        </div>
        <div className={classnames(compactStyles.column, compactStyles.buttonCta)}>
          <button
            style={ctaStyles}
            className={classnames(
              styles.button,
              compactStyles.button,
              css(`
                    color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                    text-transform: capitalize;
                    &:hover {opacity: 0.84;}
                  `),
            )}
            onClick={depositsOfferCta}
          >
            Open Account
          </button>
        </div>
        <div className={styles.disclaimerRow}>
          {!!disclaimerModalVisible && (
            <DisclaimerModal
              offset={disclaimerModalOffset}
              toggleModal={toggleDisclaimerModal}
              disclaimerMoreInfo={disclaimerMoreInfo}
              disclaimerDetails={disclaimerDetails}
            />
          )}
          <div
            className={classnames(
              styles.label,
              styles.disclaimerLink,
              compactStyles.disclaimerLink,
              css(`color: ${colorTextSecondary}`),
            )}
            onClick={toggleDisclaimerModal}
          >
            Disclaimer &amp; more info
          </div>
        </div>
      </div>
      {customPixel}
    </li>
  )

  const listItem = (
    <li
      className={classnames(
        PublicClassNames.Offer,
        styles.offer,
        css`
          border-color: ${colorBorder};
          border-radius: ${borderRadiusLarge};
        `,
      )}
      ref={el}
      data-test-val={isDev ? offer.uuid : ''}
    >
      <TrackingPixel leadUuid={leadUuid} rateTableUuid={rateTableUuid} offerUuid={offer.uuid} />
      <div className={styles.productDetails}>
        <div className={classnames(styles.label, styles.productSubtype, css(`color: ${colorTextSecondary}`))}>
          {productSubTypeLabel}
        </div>
        <div className={classnames(styles.label, styles.effectiveDate, css(`color: ${colorTextSecondary}`))}>
          As of {effectiveAsOfDate}
        </div>
      </div>
      <div className={classnames(styles.offerWrapper, styles.depositsOfferWrapper)}>
        <div className={classnames(styles.column, styles.originator, styles.depositsColumn)}>
          <PartnerLogo dispatchOfferCta={dispatchOfferCta} offer={offer} offerStyleIsCompact={offerStyleIsCompact} />
        </div>
        <div className={classnames(styles.column, styles.depositsColumn)}>
          <div
            className={classnames(
              styles.stat,
              styles.statBold,
              css`
                font-family: ${fontFamilyPrimary};
              `,
            )}
          >
            <span data-test-id="offer_apy">{annualPercentYield}</span>
          </div>
          <p className={classnames(styles.label, css(`color: ${colorTextSecondary}`))}>APY</p>
        </div>
        <div className={classnames(styles.column, styles.depositsColumn, styles.depositsFeatures)}>
          <div
            className={classnames(
              css`
                font-family: ${fontFamilyPrimary};
              `,
            )}
          >
            <div className={styles.detailsCol}>
              {monthlyFees !== undefined && (
                <div>
                  Monthly fee: <span data-test-id="offer_monthlyFee">{monthlyFees}</span>
                </div>
              )}
              {checkWriting !== undefined && (
                <div>
                  Check writing: <span data-test-id="offer_checkWriting">{checkWriting}</span>
                </div>
              )}
            </div>
            <div className={styles.detailsCol}>
              {avoidFees !== undefined && (
                <div>
                  Min balance to avoid fees: <span data-test-id="offer_minBalanceToAvoidFees">{avoidFees}</span>
                </div>
              )}
              <div>
                Min balance to earn APY: <span data-test-id="offer_minBalanceForAPY">{minimumDeposit}</span>
              </div>
            </div>
          </div>
        </div>
        <div className={classnames(styles.column, styles.cta, styles.depositsColumn)}>
          <button
            className={classnames(
              styles.button,
              css(`
                color: ${getContrastingColor(ctaStyles?.backgroundColor, { black: colorTextPrimary })};
                border-radius: ${borderRadiusSmall};
                text-transform: capitalize;
                &:hover {opacity: 0.84;}
              `),
            )}
            style={ctaStyles}
            onClick={depositsOfferCta}
          >
            Open Account
          </button>
        </div>
      </div>
      {customPixel}
      <div className={styles.disclaimerRow}>
        {!!disclaimerModalVisible && (
          <DisclaimerModal
            offset={disclaimerModalOffset}
            toggleModal={toggleDisclaimerModal}
            disclaimerMoreInfo={disclaimerMoreInfo}
            disclaimerDetails={disclaimerDetails}
          />
        )}
        <div
          className={classnames(
            styles.label,
            styles.disclaimerLink,
            styles.disclaimerLinkRight,
            css(`color: ${colorTextSecondary}`),
          )}
          onClick={toggleDisclaimerModal}
        >
          Disclaimer &amp; more info
        </div>
      </div>
    </li>
  )

  if (offerStyleIsCompact) {
    return compactListItem
  } else {
    return listItem
  }
}

const mapStateToProps = (state: CommonState, ownProps: OwnProps) => ({
  ...ownProps,
  leadUuid: state.rates[Product.Deposits].leadUuid,
  offerStyleIsCompact: offerStyleIsCompactSelector(state),
  rateTableUuid: state.rates[Product.Deposits].uuid,
})

const mapDispatchToProps = (dispatch) => ({
  dispatchLeadInitialized: (leadUuid: string) => dispatch(leadInitialized({ uuid: leadUuid })),
})

export default connect(mapStateToProps, mapDispatchToProps)(ListItem)
